import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { PLANET_RADIUS, latLonToVector3, orientOnSphere, tangentBasis } from "./sphere";

const EXPLORER_URL = `${import.meta.env.BASE_URL}models/henry-explorer.glb`;
const WALK_URL = `${import.meta.env.BASE_URL}models/henry-explorer-walk.glb`;

export class Player {
  readonly group = new THREE.Group();
  moving = false;
  private visual = new THREE.Group();
  private leftLeg: THREE.Object3D | null = null;
  private rightLeg: THREE.Object3D | null = null;
  private leftArm: THREE.Object3D | null = null;
  private rightArm: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private walkAction: THREE.AnimationAction | null = null;
  private procedural = true;
  private reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  constructor() {
    this.group.name = "henry";
    this.visual.name = "visual";
    this.group.add(this.visual);
    this.attachPlaceholder();
    const tag = document.createElement("div");
    tag.className = "player-label";
    tag.textContent = "Henry";
    const label = new CSS2DObject(tag);
    label.position.set(0, 1.55, 0);
    this.group.add(label);
    const spawn = latLonToVector3(12, 0, PLANET_RADIUS);
    orientOnSphere(this.group, spawn, tangentBasis(spawn).north);
    void this.tryLoadModels();
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }

  update(dt: number, time: number): void {
    if (this.mixer && this.walkAction) {
      if (this.moving && !this.reducedMotion) {
        this.walkAction.paused = false;
        if (!this.walkAction.isRunning()) {
          this.walkAction.reset().play();
        }
      } else {
        this.walkAction.paused = true;
        this.walkAction.time = 0;
      }
      this.mixer.update(dt);
      return;
    }
    if (!this.procedural) {
      return;
    }
    const amp = this.moving && !this.reducedMotion ? 1 : this.reducedMotion ? 0 : 0.12;
    this.visual.position.y = Math.sin(time * 11) * 0.045 * (this.moving ? 1 : amp * 0.35);
    const swing = Math.sin(time * 11) * 0.55 * (this.moving && !this.reducedMotion ? 1 : 0);
    if (this.leftLeg) {
      this.leftLeg.rotation.x = swing;
    }
    if (this.rightLeg) {
      this.rightLeg.rotation.x = -swing;
    }
    if (this.leftArm) {
      this.leftArm.rotation.x = -swing * 0.65;
    }
    if (this.rightArm) {
      this.rightArm.rotation.x = swing * 0.65;
    }
  }

  private async tryLoadModels(): Promise<void> {
    const loader = new GLTFLoader();
    const walk = await loadGltf(loader, WALK_URL);
    const explorer = walk ? null : await loadGltf(loader, EXPLORER_URL);
    const gltf = walk ?? explorer;
    if (!gltf) {
      return;
    }
    const model = prepareModel(gltf.scene);
    this.visual.clear();
    this.visual.add(model);
    const clips = gltf.animations;
    if (clips.length > 0) {
      this.mixer = new THREE.AnimationMixer(model);
      const clip = clips[0];
      if (clip) {
        this.walkAction = this.mixer.clipAction(clip);
        this.walkAction.play();
        this.walkAction.paused = true;
        this.procedural = false;
        this.leftLeg = null;
        this.rightLeg = null;
        this.leftArm = null;
        this.rightArm = null;
        return;
      }
    }
    // 无骨骼动画时：挂上模型里的四肢名，走程序化步态
    this.procedural = true;
    this.leftLeg = model.getObjectByName("leftLeg") ?? null;
    this.rightLeg = model.getObjectByName("rightLeg") ?? null;
    this.leftArm = model.getObjectByName("leftArm") ?? null;
    this.rightArm = model.getObjectByName("rightArm") ?? null;
  }

  private attachPlaceholder(): void {
    const root = makePlaceholder();
    this.leftLeg = root.getObjectByName("leftLeg") ?? null;
    this.rightLeg = root.getObjectByName("rightLeg") ?? null;
    this.leftArm = root.getObjectByName("leftArm") ?? null;
    this.rightArm = root.getObjectByName("rightArm") ?? null;
    this.visual.add(root);
  }
}

async function loadGltf(loader: GLTFLoader, url: string): Promise<GLTF | null> {
  try {
    return await loader.loadAsync(url);
  } catch {
    return null;
  }
}

function prepareModel(scene: THREE.Object3D): THREE.Object3D {
  const box = new THREE.Box3().setFromObject(scene);
  const size = box.getSize(new THREE.Vector3());
  const scale = 1.2 / Math.max(size.y, 0.001);
  scene.scale.setScalar(scale);
  box.setFromObject(scene);
  scene.position.y = -box.min.y;
  scene.traverse((child: THREE.Object3D) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
  return scene;
}

function makePlaceholder(): THREE.Group {
  const root = new THREE.Group();
  const coat = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.42, 6, 12), new THREE.MeshStandardMaterial({ color: 0x2a3a42, roughness: 0.7 }));
  coat.position.y = 0.55;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 14, 12), new THREE.MeshStandardMaterial({ color: 0xd4b49a, roughness: 0.55 }));
  head.position.y = 1.05;
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.21, 12, 10), new THREE.MeshStandardMaterial({ color: 0x2b241c }));
  hair.position.y = 1.14;
  hair.scale.set(1, 0.55, 1);
  const goggleL = new THREE.Mesh(new THREE.TorusGeometry(0.055, 0.012, 8, 12), new THREE.MeshStandardMaterial({ color: 0xc4a35a, metalness: 0.4, roughness: 0.35 }));
  const goggleR = goggleL.clone();
  goggleL.position.set(-0.07, 1.06, 0.16);
  goggleR.position.set(0.07, 1.06, 0.16);
  const pack = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.32, 0.12), new THREE.MeshStandardMaterial({ color: 0x3a3228 }));
  pack.position.set(0, 0.62, -0.22);
  const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.22, 6), new THREE.MeshStandardMaterial({ color: 0xc4a35a }));
  antenna.position.set(0.08, 0.84, -0.22);

  const leftLeg = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.22, 4, 8), new THREE.MeshStandardMaterial({ color: 0x1c2428 }));
  const rightLeg = leftLeg.clone();
  leftLeg.name = "leftLeg";
  rightLeg.name = "rightLeg";
  leftLeg.position.set(-0.1, 0.18, 0);
  rightLeg.position.set(0.1, 0.18, 0);
  const leftArm = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.22, 4, 8), new THREE.MeshStandardMaterial({ color: 0x2a3a42 }));
  const rightArm = leftArm.clone();
  leftArm.name = "leftArm";
  rightArm.name = "rightArm";
  leftArm.position.set(-0.3, 0.62, 0);
  rightArm.position.set(0.3, 0.62, 0);

  root.add(coat, head, hair, goggleL, goggleR, pack, antenna, leftLeg, rightLeg, leftArm, rightArm);
  return root;
}
